import { useCallback, useEffect, useState } from "react";
import { ChatCircle, CheckCircle, Circle, Copy, CreditCard, DeviceMobile, Envelope, FacebookLogo, GoogleLogo, PaperPlaneTilt, PlugsConnected, WhatsappLogo, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import TwilioCard from "./TwilioCard";

const ICONS = { email: Envelope, sms: DeviceMobile, whatsapp: ChatCircle,
  whatsapp_api: WhatsappLogo, stripe: CreditCard, meta_ads: FacebookLogo };

function MetaAdsCard({ s, canManage, onChanged }) {
  const [open, setOpen] = useState(false);
  const [datasetId, setDatasetId] = useState("");
  const [token, setToken] = useState("");
  const [testCode, setTestCode] = useState("");
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
  const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";

  const save = async () => {
    if (!datasetId.trim()) { toast.error("Inserisci l'ID Dataset / Pixel"); return; }
    setBusy(true);
    try {
      await api.put("/integrations/meta_ads", {
        provider: "ristorante",
        credentials: token.trim() ? { capi_access_token: token.trim() } : {},
        config: { dataset_id: datasetId.trim(), test_event_code: testCode.trim(), capi_enabled: "true" },
      });
      toast.success("Meta Ads collegato: gli eventi Lead e Prenotazione partiranno dal server");
      setOpen(false); setToken(""); onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/meta_ads/test");
      if (r.data.ok) toast.success(r.data.message); else toast.error(r.data.message);
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="service-meta-ads">
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          s?.attivo ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-400"}`}>
          <FacebookLogo size={20} weight="bold" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-zinc-900">Meta Ads (Conversioni)</span>
            {s?.attivo ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1"
                data-testid="service-status-meta_ads">
                <CheckCircle size={10} weight="fill" /> ATTIVO
              </span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-zinc-100 text-zinc-500 border-zinc-300 flex items-center gap-1"
                data-testid="service-status-meta_ads">
                <Circle size={10} /> NON ATTIVO
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Invia a Meta gli eventi server-side <b>Lead</b> (download coupon) e <b>Schedule</b> (prenotazione),
            con deduplica del Pixel: le inserzioni si ottimizzano su chi prenota davvero.
          </p>
        </div>
      </div>
      {canManage && (
        <div className="mt-3 space-y-2.5">
          {!open ? (
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setOpen(true)} data-testid="meta-ads-configure"
                className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold">
                {s?.attivo ? "Riconfigura" : "Collega il tuo Pixel"}
              </button>
              {s?.attivo && (
                <button onClick={test} disabled={busy} data-testid="meta-ads-test"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-600 disabled:opacity-40">
                  {busy ? "Test..." : "Test connessione"}
                </button>
              )}
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-md p-3 space-y-2.5" data-testid="meta-ads-form">
              <div>
                <label className={labelCls}>ID Dataset / Pixel</label>
                <input value={datasetId} onChange={(e) => setDatasetId(e.target.value)} placeholder="Es. 858113204027685"
                  className={inputCls} data-testid="meta-ads-dataset" />
              </div>
              <div>
                <label className={labelCls}>Token API Conversioni</label>
                <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
                  placeholder={s?.attivo ? "Salvato — lascia vuoto per mantenerlo" : "EAA…"}
                  className={inputCls} data-testid="meta-ads-token" autoComplete="off" />
              </div>
              <div>
                <label className={labelCls}>Codice Test Eventi (opzionale)</label>
                <input value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="Es. TEST12345 — solo per le prove"
                  className={inputCls} data-testid="meta-ads-test-code" />
              </div>
              <p className="text-[11px] text-zinc-400">
                Events Manager → il tuo Pixel → Impostazioni → API per le conversioni → Genera token di accesso.
              </p>
              <div className="flex gap-2">
                <button onClick={save} disabled={busy} data-testid="meta-ads-save"
                  className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold disabled:opacity-40">
                  {busy ? "Salvataggio..." : "Salva e attiva"}
                </button>
                <button onClick={() => setOpen(false)} data-testid="meta-ads-cancel"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-600">
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WaBusinessCard({ s, canManage, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [mockPrompt, setMockPrompt] = useState(false);
  const [metaCfg, setMetaCfg] = useState(null);
  const [devOpen, setDevOpen] = useState(false);
  const [devForm, setDevForm] = useState({ phone_number_id: "", access_token: "", phone_number: "", waba_id: "" });
  const [testTo, setTestTo] = useState("");
  const [wabaManual, setWabaManual] = useState("");
  const [wabaOpen, setWabaOpen] = useState(false);

  useEffect(() => {
    api.get("/integrations/whatsapp/meta/config").then((r) => setMetaCfg(r.data)).catch(() => {});
  }, []);

  const doConnect = async (code = null, signupIds = {}) => {
    setBusy(true);
    try {
      // redirect_uri identico a quello della finestra OAuth: il FB SDK usa
      // l'URL completo della pagina (query inclusa), non solo origin+path
      const redirectUri = window.location.href.split("#")[0];
      const r = await api.post("/integrations/whatsapp/meta/connect",
        code ? { code, ...signupIds, redirect_uri: redirectUri } : {});
      toast.success(r.data.mock
        ? "Connessione di prova attivata (modalità test)"
        : `WhatsApp Business collegato: ${r.data.phone_number}`);
      setMockPrompt(false);
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const connect = () => {
    if (!metaCfg) { toast.error("Configurazione in caricamento, riprova tra un secondo"); return; }
    if (metaCfg.mock) { setMockPrompt(true); return; }
    // Redirect OAuth con redirect_uri FISSO (/whatsapp-callback, registrato nella
    // Meta App): il dialogo usa esattamente questo URL, quindi lo scambio del code
    // non può fallire per mismatch.
    const baseState = Math.random().toString(36).slice(2) + Date.now().toString(36);
    // Il WABA ID manuale viaggia DENTRO lo state OAuth (suffisso --w<ID>): Meta lo
    // rimanda identico nel callback, cosi' sopravvive alla partizione dello storage
    // di Safari tra iframe e nuova scheda (dove localStorage non e' condiviso).
    const waba = wabaManual.trim();
    const state = baseState + (waba ? `--w${waba}` : "");
    // localStorage (non sessionStorage): se l'app e' in un iframe (anteprima nella chat)
    // Meta si apre in una NUOVA scheda, che non condividerebbe la sessionStorage.
    // Lista (non singolo valore): click ripetuti non invalidano i tentativi precedenti.
    // Nella lista va il BASE state: al callback il suffisso --w viene rimosso.
    try {
      const states = JSON.parse(localStorage.getItem("wa_oauth_states") || "[]");
      states.push(baseState);
      localStorage.setItem("wa_oauth_states", JSON.stringify(states.slice(-5)));
      if (waba) localStorage.setItem("wa_oauth_waba", waba);
      else localStorage.removeItem("wa_oauth_waba");
    } catch { /* storage partizionato (Safari iframe): il callback procede lo stesso */ }
    const redirectUri = `${window.location.origin}/whatsapp-callback`;
    // extras sessionInfoVersion=3: attiva l'Embedded Signup via config_id
    // (Facebook Login for Business: scelta business/WABA/numero).
    // NON aggiungere feature:"whatsapp_embedded_signup": e' riservato alle app
    // registrate come BSP/Tech Provider Meta → errore "app non disponibile".
    const extras = encodeURIComponent(JSON.stringify({ featureType: "", sessionInfoVersion: "3" }));
    const url = `https://www.facebook.com/v25.0/dialog/oauth?client_id=${metaCfg.app_id}`
      + `&redirect_uri=${encodeURIComponent(redirectUri)}`
      + `&response_type=code&config_id=${metaCfg.config_id}&state=${state}&extras=${extras}`;
    // Dentro un iframe la navigazione a facebook.com viene bloccata (X-Frame-Options: DENY)
    // e il browser mostra una pagina di errore nera: apri Meta in una nuova scheda.
    if (window.self !== window.top) {
      window.open(url, "_blank", "noopener");
      toast.success("Ho aperto Meta in una nuova scheda: completa lì il collegamento");
      return;
    }
    window.location.href = url;
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/whatsapp/meta/verify");
      if (r.data.ok) {
        toast.success(r.data.mock
          ? "Connessione di prova attiva (modalità test)"
          : `Connessione verificata: ${r.data.verified_name} · ${r.data.phone_number}`);
      } else {
        toast.error(r.data.detail || "Connessione in errore");
      }
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const testSend = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/whatsapp/meta/test", testTo.trim() ? { to: testTo.trim() } : {});
      toast.success(r.data.mock
        ? `Invio di prova SIMULATO verso ${r.data.to} (modalità test)`
        : `Messaggio di prova inviato a ${r.data.to}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const devConnect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/integrations/whatsapp/meta/dev-connect", {
        phone_number_id: devForm.phone_number_id.trim(),
        access_token: devForm.access_token.trim(),
        phone_number: devForm.phone_number.trim() || null,
        waba_id: devForm.waba_id.trim() || null,
      });
      toast.success("WhatsApp Business collegato");
      setDevOpen(false);
      setDevForm({ phone_number_id: "", access_token: "", phone_number: "", waba_id: "" });
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare WhatsApp Business? Le notifiche WhatsApp torneranno al canale piattaforma.")) return;
    setBusy(true);
    try {
      await api.delete("/integrations/whatsapp_api");
      toast.success("WhatsApp Business scollegato");
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="service-whatsapp_api">
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          s.attivo ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-400"}`}>
          <WhatsappLogo size={20} weight="bold" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-zinc-900">{s.label}</span>
            {s.attivo ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1"
                data-testid="service-status-whatsapp_api">
                <CheckCircle size={10} weight="fill" /> ATTIVO
              </span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-zinc-100 text-zinc-500 border-zinc-300 flex items-center gap-1"
                data-testid="service-status-whatsapp_api">
                <Circle size={10} /> NON ATTIVO
              </span>
            )}
            {s.wa_mock && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300"
                data-testid="wa-mock-badge">MODALITÀ TEST</span>
            )}
            {s.wa_dev && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-sky-100 text-sky-800 border-sky-300"
                data-testid="wa-dev-badge">TOKEN MANUALE</span>
            )}
            {s.wa_error && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-rose-100 text-rose-700 border-rose-300"
                data-testid="wa-error-badge">ERRORE — DA RICOLLEGARE</span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-1">{s.description}</p>
          {s.attivo && s.wa_phone && (
            <p className="text-[11px] text-zinc-400 mt-1.5" data-testid="wa-connection-info">
              {s.wa_name} · {s.wa_phone} · Token salvato cifrato, mai visibile
            </p>
          )}
        </div>
      </div>

      {canManage && (
        <div className="mt-3 pl-13 space-y-2">
          {mockPrompt && !s.attivo && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="wa-mock-prompt">
              <p className="text-xs text-amber-800">
                La piattaforma non è ancora collegata a Meta: puoi attivare una <b>connessione di prova</b> per
                vedere il flusso completo. Quando Dishenda attiverà l'app Meta, questo pulsante aprirà la
                finestra ufficiale Meta per collegare il tuo vero WhatsApp Business.
              </p>
              <div className="flex gap-2">
                <button onClick={() => doConnect()} disabled={busy} data-testid="wa-mock-connect"
                  className="h-9 px-3 rounded-md bg-amber-600 text-white text-xs font-bold disabled:opacity-40">
                  Attiva connessione di prova
                </button>
                <button onClick={() => setMockPrompt(false)} data-testid="wa-mock-cancel"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-600 flex items-center gap-1">
                  <X size={13} /> Annulla
                </button>
              </div>
            </div>
          )}
          {devOpen && !s.attivo && (
            <form onSubmit={devConnect} className="bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-2" data-testid="wa-dev-form">
              <p className="text-xs text-sky-800">
                <b>Collegamento manuale</b>: incolla Phone number ID, token permanente e WABA ID (funziona sia col numero di test Meta sia col numero reale del ristorante).
                Solo per i test della piattaforma: i ristoranti useranno il pulsante "Collega WhatsApp Business".
              </p>
              <input required value={devForm.phone_number_id} placeholder="Phone number ID — numerico (es. 1336663259522766), NON il numero di telefono" data-testid="wa-dev-pnid"
                onChange={(e) => setDevForm({ ...devForm, phone_number_id: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
              <input required type="password" value={devForm.access_token} placeholder="Token di accesso temporaneo (inizia con EAA...)" data-testid="wa-dev-token"
                autoComplete="off" data-lpignore="true" data-1p-ignore="true"
                onChange={(e) => setDevForm({ ...devForm, access_token: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
              <input value={devForm.phone_number} placeholder="Numero di test (es. +1 555 000 1111)" data-testid="wa-dev-phone"
                onChange={(e) => setDevForm({ ...devForm, phone_number: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
              <input value={devForm.waba_id} placeholder="WABA ID — per i template (WhatsApp Business Account ID)" data-testid="wa-dev-waba"
                onChange={(e) => setDevForm({ ...devForm, waba_id: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
              <p className="text-[11px] text-sky-700">
                Il WABA ID è in Meta App → WhatsApp → <b>Impostazioni API</b> (voce "WhatsApp Business Account ID", sotto il numero di test). Serve per gestire i template messaggi.
              </p>
              <div className="flex gap-2">
                <button disabled={busy} data-testid="wa-dev-connect"
                  className="h-9 px-4 rounded-md bg-sky-600 text-white text-xs font-bold disabled:opacity-40">
                  Collega numero
                </button>
                <button type="button" onClick={() => setDevOpen(false)} data-testid="wa-dev-cancel"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-600">Annulla</button>
              </div>
            </form>
          )}
          <div className="flex gap-2 flex-wrap">
            {(!s.attivo || s.wa_error) && !mockPrompt && (
              <button onClick={connect} disabled={busy} data-testid="wa-connect"
                className="h-9 px-4 rounded-md bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40">
                <PlugsConnected size={15} weight="bold" /> {s.wa_error ? "Ricollega WhatsApp Business" : "Collega WhatsApp Business"}
              </button>
            )}
            {!s.attivo && !mockPrompt && !devOpen && (
              <button onClick={() => setDevOpen(true)} data-testid="wa-dev-open"
                className="h-9 px-3 rounded-md text-[11px] font-semibold text-zinc-500 underline">
                Collegamento manuale (token)
              </button>
            )}
            {(!s.attivo || s.wa_error) && !mockPrompt && !devOpen && (
              <button type="button" onClick={() => setWabaOpen(!wabaOpen)} data-testid="wa-waba-toggle"
                className="h-9 px-3 rounded-md text-[11px] font-semibold text-zinc-500 underline">
                Meta non trova il tuo account?
              </button>
            )}
            {s.attivo && s.wa_phone && !s.wa_error && (
              <>
                <input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                  placeholder="Destinatario prova (+39…)" data-testid="wa-test-to"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-xs w-44" />
                <button onClick={testSend} disabled={busy} data-testid="wa-test-send"
                  className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40">
                  <PaperPlaneTilt size={15} weight="bold" /> Invio di prova
                </button>
                <button onClick={verify} disabled={busy} data-testid="wa-verify"
                  className="h-9 px-4 rounded-md border border-zinc-300 text-zinc-700 text-xs font-bold disabled:opacity-40">
                  Verifica connessione
                </button>
                <button onClick={disconnect} disabled={busy} data-testid="wa-disconnect"
                  className="h-9 px-3 rounded-md border border-rose-300 text-rose-600 text-xs font-bold disabled:opacity-40">
                  Scollega
                </button>
              </>
            )}
          </div>
          {wabaOpen && (!s.attivo || s.wa_error) && !mockPrompt && !devOpen && (
            <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid="wa-waba-manual">
              <p className="text-[11px] text-sky-800">
                Se dopo il collegamento compare "Nessun WhatsApp Business Account condiviso",
                incolla qui il tuo <b>WhatsApp Business Account ID</b> e riprova: lo trovi in
                Meta Business Suite → <b>Impostazioni → Account WhatsApp</b>, oppure in
                Meta App → WhatsApp → <b>Impostazioni API</b>.
              </p>
              <input value={wabaManual} onChange={(e) => setWabaManual(e.target.value)}
                placeholder="WABA ID (es. 102524622829866)" data-testid="wa-waba-input"
                className="w-full h-10 px-3 rounded-md border border-sky-300 text-sm bg-white" />
              <p className="text-[11px] text-sky-700">
                Poi clicca di nuovo <b>Collega WhatsApp Business</b> e completa il dialogo Meta.
              </p>
            </div>
          )}
          {s.attivo && s.wa_dev && (
            <p className="text-[11px] text-sky-700" data-testid="wa-dev-hint">
              Numero di test Meta: il destinatario deve essere tra i numeri consentiti
              (app Meta → WhatsApp → Impostazioni API → elenco destinatari).
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SmsWalletCard({ isAdmin }) {
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState("");

  const loadWallet = useCallback(() => {
    api.get("/billing/sms").then((r) => setWallet(r.data)).catch(() => setWallet({ error: true }));
  }, []);

  useEffect(() => {
    loadWallet();
    if (new URLSearchParams(window.location.search).get("sms") === "annullato") {
      toast.info("Acquisto pacchetto SMS annullato");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadWallet]);

  const buy = async (packId) => {
    setBusy(packId);
    try {
      const r = await api.post("/billing/sms/checkout", { pack_id: packId });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy("");
    }
  };

  if (!wallet || wallet.error) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="sms-wallet-card">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-amber-50 text-amber-600">
          <DeviceMobile size={20} weight="bold" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-zinc-900">Pacchetti SMS</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300"
              data-testid="sms-balance-badge">
              SALDO: {wallet.balance} SMS
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Gli SMS di campagne e automazioni partono dal saldo prepagato e viaggiano sulla rete Dishenda.
            Acquista un pacchetto per ricaricare il saldo.
          </p>
        </div>
      </div>
      {isAdmin && wallet.stripe_ready && (
        <div className="mt-3 flex gap-2 flex-wrap" data-testid="sms-packs">
          {wallet.packs.map((p) => (
            <button key={p.pack_id} onClick={() => buy(p.pack_id)} disabled={!!busy}
              data-testid={`sms-buy-${p.pack_id}`}
              className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold disabled:opacity-40">
              {busy === p.pack_id ? "Reindirizzamento a Stripe..." :
                `${p.label} — €${p.price_eur % 1 === 0 ? p.price_eur : p.price_eur.toFixed(2)}`}
            </button>
          ))}
        </div>
      )}
      {isAdmin && !wallet.stripe_ready && (
        <p className="mt-3 text-[11px] text-zinc-400">Pagamenti non ancora attivi: contatta il supporto Dishenda per ricaricare il saldo.</p>
      )}
      {(wallet.transactions || []).length > 0 && (
        <div className="mt-3 border-t border-zinc-100 pt-2 space-y-1" data-testid="sms-transactions">
          {wallet.transactions.slice(0, 6).map((t, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-zinc-500 truncate">{t.note || t.type}</span>
              <span className={t.amount >= 0 ? "text-emerald-600 font-bold shrink-0" : "text-zinc-700 font-bold shrink-0"}>
                {t.amount >= 0 ? `+${t.amount}` : t.amount}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GoogleBookingCard({ rid, canManage }) {
  const url = `https://app.dishenda.it/p/${rid}`;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="service-google-booking">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-sky-50 text-sky-600">
          <GoogleLogo size={20} weight="bold" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-zinc-900">Prenota con Google</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-sky-100 text-sky-800 border-sky-300"
              data-testid="service-status-google-booking">LINK PRONTO</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            I clienti prenotano dal pulsante "Prenota un tavolo" su Google Maps e Ricerca:
            il profilo Google Business del locale punta al tuo modulo Dishenda e le richieste
            arrivano qui come "da confermare".
          </p>
        </div>
      </div>
      {canManage && (
        <div className="mt-3 pl-13 space-y-2.5">
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate text-[11px] bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2 text-zinc-600"
              data-testid="google-booking-url">{url}</code>
            <button data-testid="google-booking-copy"
              onClick={() => navigator.clipboard.writeText(url)
                .then(() => toast.success("Link di prenotazione copiato"))
                .catch(() => toast.error("Copia non riuscita: seleziona il link manualmente"))}
              className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 shrink-0">
              <Copy size={13} weight="bold" /> Copia
            </button>
          </div>
          <ol className="text-[11px] text-zinc-500 list-decimal pl-4 space-y-1">
            <li>Apri il profilo Google Business del locale su <b>business.google.com</b></li>
            <li>Vai su <b>Prenotazioni</b> (o "Link per prenotare") e incolla il link copiato</li>
            <li>Salva: entro pochi giorni il pulsante "Prenota un tavolo" appare su Maps e Ricerca</li>
          </ol>
          <p className="text-[11px] text-zinc-400">
            In arrivo: prenotazione nativa dentro Google (Reserve with Google) — Dishenda sta
            preparando l'accreditamento come partner ufficiale Google.
          </p>
        </div>
      )}
    </div>
  );
}


export default function ServicesStatus() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [role, setRole] = useState(null);
  const [rid, setRid] = useState(null);

  const load = useCallback(() => {
    setError(false);
    api.get("/integrations/services").then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/auth/me").then((r) => { setRole(r.data.role); setRid(r.data.restaurant_id); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 text-center" data-testid="services-error">
        <p className="text-sm text-rose-700 mb-3">Errore nel caricamento dei servizi</p>
        <button onClick={load} className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold">Riprova</button>
      </div>
    );
  }
  if (!data) return <div className="text-sm text-zinc-400 py-8 text-center" data-testid="services-loading">Caricamento...</div>;

  const canManage = role === "admin" || role === "manager";
  const wa = data.services.find((s) => s.key === "whatsapp_api");
  const sms = data.services.find((s) => s.key === "sms");
  const waTwilio = data.services.find((s) => s.key === "whatsapp");
  const metaAds = data.services.find((s) => s.key === "meta_ads");
  const others = data.services.filter((s) => !["whatsapp_api", "sms", "whatsapp", "meta_ads"].includes(s.key));

  return (
    <div className="space-y-4" data-testid="services-status">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-xs text-sky-800">
        I servizi di comunicazione e pagamento sono <b>configurati e gestiti da Dishenda</b>. Puoi collegare direttamente il tuo <b>WhatsApp Business</b> con il pulsante dedicato: le notifiche di prenotazione partiranno dal tuo numero.
      </div>
      <div className="space-y-3">
        {wa && <WaBusinessCard s={wa} canManage={canManage} onChanged={load} />}
        {rid && <GoogleBookingCard rid={rid} canManage={canManage} />}
        <MetaAdsCard s={metaAds} canManage={canManage} onChanged={load} />
        <TwilioCard sms={sms} whatsapp={waTwilio} canManage={canManage} onChanged={load} />
        <SmsWalletCard isAdmin={role === "admin"} />
        {others.map((s) => {
          const Icon = ICONS[s.key] || Envelope;
          return (
            <div key={s.key} className="bg-white border border-zinc-200 rounded-lg p-4 flex items-start gap-3"
              data-testid={`service-${s.key}`}>
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                s.attivo ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-400"}`}>
                <Icon size={20} weight="bold" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-zinc-900">{s.label}</span>
                  {s.attivo ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1"
                      data-testid={`service-status-${s.key}`}>
                      <CheckCircle size={10} weight="fill" /> ATTIVO
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-zinc-100 text-zinc-500 border-zinc-300 flex items-center gap-1"
                      data-testid={`service-status-${s.key}`}>
                      <Circle size={10} /> NON ATTIVO
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-500 mt-1">{s.description}</p>
                {s.attivo && (
                  <p className="text-[11px] text-zinc-400 mt-1.5">
                    {s.gestione}
                    {s.last_verified_at && ` · Ultima verifica: ${new Date(s.last_verified_at).toLocaleString("it-IT")}`}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-400">Per attivare gli altri servizi o modificare la configurazione contatta il supporto Dishenda.</p>
    </div>
  );
}
